import {
  currentRouteTarget,
  resolveParent,
  resolveSaveTo,
  routeKey,
  type RouteResolverContext,
  type RouteTarget,
} from "../routes";

export type DocReturnTo = {
  tab: string;
  sub?: string | null;
  name?: string;
};

export type DocSuccessMode = "draftable" | "review";

export type NavigateOpts = {
  returnTo?: DocReturnTo | null;
  skipPush?: boolean;
  docSuccessMode?: DocSuccessMode;
  resolverContext?: RouteResolverContext;
};

type ApplyFilter = (q: string) => void;

type NavigateFn = (
  tab: string,
  sub: string | null,
  params: Record<string, string>,
  opts?: NavigateOpts
) => void;

let navigateFn: NavigateFn | null = null;
let returnStack: RouteTarget[] = [];
let docReturnTo: DocReturnTo | null = null;
let docSuccessMode: DocSuccessMode = "review";
let applyInvoiceFilter: ApplyFilter | null = null;
let applyQuoteFilter: ApplyFilter | null = null;

export function bindRouterNavigate(fn: NavigateFn) {
  navigateFn = fn;
}

function nav(
  tab: string,
  sub: string | null = null,
  params: Record<string, string> = {},
  opts?: NavigateOpts
) {
  if (!navigateFn) throw new Error("Router navigate not bound");
  navigateFn(tab, sub, params, opts);
}

export function getDocSuccessMode(): DocSuccessMode {
  return docSuccessMode;
}

export function setDocSuccessMode(mode: DocSuccessMode) {
  docSuccessMode = mode;
}

export function registerInvoiceListFilter(apply: ApplyFilter) {
  applyInvoiceFilter = apply;
}

export function registerQuoteListFilter(apply: ApplyFilter) {
  applyQuoteFilter = apply;
}

export function openInvoicesFiltered(q: string) {
  clearDocReturnTo();
  applyInvoiceFilter?.(String(q || "").trim());
  nav("invoices", null, {}, { skipPush: true });
}

export function openQuotesFiltered(q: string) {
  clearDocReturnTo();
  applyQuoteFilter?.(String(q || "").trim());
  nav("quotes", null, {}, { skipPush: true });
}

export function setDocReturnTo(target: DocReturnTo | null) {
  docReturnTo = target;
}

export function clearDocReturnTo() {
  docReturnTo = null;
}

export function consumeDocReturnTo(): DocReturnTo | null {
  const target = docReturnTo;
  docReturnTo = null;
  return target;
}

export function navigateDocReturnOr(fallback: () => void) {
  const target = consumeDocReturnTo();
  if (!target) {
    fallback();
    return;
  }
  nav(
    target.tab,
    target.sub ?? null,
    target.name ? { name: target.name } : {},
    { skipPush: true }
  );
}

export function openInvoiceDoc(invoiceId: string, returnTo?: DocReturnTo | null) {
  const id = String(invoiceId || "").trim();
  if (!id) return;
  if (returnTo === undefined) setDocReturnTo({ tab: "invoices" });
  else setDocReturnTo(returnTo);
  nav("invoices", "success", { name: id }, {
    skipPush: true,
    docSuccessMode: "review",
  });
}

export function openQuoteDoc(quoteId: string, returnTo?: DocReturnTo | null) {
  const id = String(quoteId || "").trim();
  if (!id) return;
  if (returnTo === undefined) setDocReturnTo({ tab: "quotes" });
  else setDocReturnTo(returnTo);
  nav("quotes", "success", { name: id }, {
    skipPush: true,
    docSuccessMode: "review",
  });
}

export function navigateWithReturn(
  tab: string,
  sub: string | null = null,
  params: Record<string, string> = {},
  returnTo?: DocReturnTo | null
) {
  if (returnTo !== undefined) setDocReturnTo(returnTo);
  nav(tab, sub, params);
}

/** Called by router before updating route state. */
export function prepareNavigate(
  from: RouteTarget,
  to: RouteTarget,
  opts?: NavigateOpts
) {
  if (opts?.returnTo !== undefined) {
    setDocReturnTo(opts.returnTo);
  }
  if (opts?.docSuccessMode) {
    docSuccessMode = opts.docSuccessMode;
  }
  if (!opts?.skipPush) {
    const fromKey = routeKey(from.tab, from.sub);
    const toKey = routeKey(to.tab, to.sub);
    if (fromKey !== toKey || from.params.name !== to.params.name) {
      returnStack.push({ ...from, params: { ...from.params } });
      if (returnStack.length > 8) returnStack.shift();
    }
  }
}

export function getBackTarget(
  current: RouteTarget,
  ctx?: RouteResolverContext
): RouteTarget {
  // Hierarchical parent wins (e.g. logo → general). The return stack is only for
  // screens without a configured parent, and is often stale because hash <a>
  // navigations never push onto it.
  const parent = resolveParent(current, ctx);
  if (parent) return parent;
  if (returnStack.length > 0) {
    const top = returnStack[returnStack.length - 1];
    return { ...top, params: { ...top.params } };
  }
  return { tab: current.tab, sub: null, params: {} };
}

export function getSaveTarget(
  current: RouteTarget,
  ctx?: RouteResolverContext
): RouteTarget {
  const saveTo = resolveSaveTo(current, ctx);
  if (saveTo) return saveTo;
  return getBackTarget(current, ctx);
}

export function popReturnStack(): RouteTarget | null {
  return returnStack.pop() ?? null;
}

export function clearReturnStack() {
  returnStack = [];
}

export function goBack(current: RouteTarget, ctx?: RouteResolverContext) {
  const parent = resolveParent(current, ctx);
  const target = getBackTarget(current, ctx);
  // Only consume the stack when it was the source of the back target.
  if (!parent) popReturnStack();
  nav(target.tab, target.sub, target.params, { skipPush: true });
}

export function goAfterSave(current: RouteTarget, ctx?: RouteResolverContext) {
  const saveTo = resolveSaveTo(current, ctx);
  const target = getSaveTarget(current, ctx);
  if (!saveTo && !resolveParent(current, ctx)) popReturnStack();
  nav(target.tab, target.sub, target.params, { skipPush: true });
}

export function goToTarget(target: RouteTarget) {
  nav(target.tab, target.sub, target.params, { skipPush: true });
}
